import './App.css';
import { useState, useEffect } from 'react';
import { useData } from './hooks/useData';
import { useCalculator } from './hooks/useCalculator';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { LocaleProvider, useLocale } from './contexts/LocaleContext';
import { SectionCard } from './components/SectionCard/SectionCard';
import { Calculator } from './components/Calculator/Calculator';
import { Summary } from './components/Summary/Summary';
import { CurrencySelector } from './components/CurrencySelector/CurrencySelector';
import { LocaleSelector } from './components/LocaleSelector/LocaleSelector';
import { ModeSelector } from './components/ModeSelector/ModeSelector';
import { AgreementPopup } from './components/AgreementPopup/AgreementPopup';
import { AgreementButton } from './components/AgreementButton';
import { QuestionsList } from './components/QuestionsList';
import { ProjectDescriptionCard } from './components/ProjectDescriptionCard';
import { t } from './locales/translations';

function AppContent() {
  const { companyName, sections, options, defaultOptions, modes, modesLabel, projectDescriptionConfig, isLoading, error } = useData();
  const { selectedLocale } = useLocale();
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<string>('');

  // Set default mode when data is loaded
  useEffect(() => {
    if (modes && modes.length > 0 && !selectedMode) {
      setSelectedMode(modes[0].id);
    }
  }, [modes, selectedMode]);

  const {
    selectedOptions,
    selectedOptionsWithQuantities,
    defaultOptions: calculatorDefaultOptions,
    totalPrices,
    projectDeadline,
    hourlyOptions,
    handleOptionToggle,
    updateOptionQuantity,
    getOptionQuantity,
    clearSelections,
    validateSelections
  } = useCalculator(sections, defaultOptions.filter(opt => !opt.allowed_modes || opt.allowed_modes.includes(selectedMode)), options);

  const filteredSections = sections.filter(section =>
    !section.visible_on_mode || section.visible_on_mode.includes(selectedMode)
  );

  if (isLoading) {
    return (
      <div className="app">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>{t('loading', selectedLocale)}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="error">
          <h2>{t('error', selectedLocale)}</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // const validation = validateSelections();

  return (
    <div className="app">
      {/* Fixed Header with Controls */}
      <header className="app-fixed-header">
        <div className="container">
          <div className="fixed-header-content">
            <div className="header-branding">
              <a
                href="https://irlen.kz"
                target="_blank"
                rel="noopener noreferrer"
                className="header-logo-link"
              >
                <img src="/IRL_logo.png" alt="IRL Logo" className="header-logo-small" />
              </a>
              <div className="header-text-compact">
                <h1 className="app-title-compact">{t('appTitle', selectedLocale)}</h1>
                <p className="app-subtitle-compact">{companyName}</p>
              </div>
            </div>
            <div className="header-controls">
              <CurrencySelector />
              <LocaleSelector />
            </div>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="container">
          <div className="content-grid">
            <div className="sections-panel">
              {/* Buttons Row */}
              <div className="buttons_row">
                <a
                  href="https://irlen.kz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="header-back-link"
                >
                  ← {t('toMainWebsite', selectedLocale)}
                </a>
                <AgreementButton onClick={() => setIsAgreementOpen(true)} />
              </div>

              {/* Questions Section - displays after header and before sections */}
              <QuestionsList locale={selectedLocale} />

              {/* Project Description Section */}
              {projectDescriptionConfig && (
                <ProjectDescriptionCard
                  config={projectDescriptionConfig}
                  locale={selectedLocale}
                />
              )}

              <div className="mode-selector-wrapper" style={{ marginBottom: '20px' }}>
                <ModeSelector
                  modes={modes || []}
                  selectedMode={selectedMode}
                  onSelectMode={setSelectedMode}
                  label={modesLabel}
                />
              </div>

              {/* <h2 className="panel-title">{t('technologyStack', selectedLocale)}</h2> */}
              <div className="sections-list">
                {filteredSections.map(section => (
                  <SectionCard
                    key={section.id}
                    section={section}
                    options={options}
                    selectedOptions={selectedOptions}
                    onOptionToggle={handleOptionToggle}
                    onOptionQuantityChange={updateOptionQuantity}
                    getOptionQuantity={getOptionQuantity}
                  />
                ))}
              </div>
            </div>

            <div className="sidebar-panel">
              <div className="sticky_wrapper">
                <Calculator
                  selectedOptions={selectedOptions}
                  selectedOptionsWithQuantities={selectedOptionsWithQuantities}
                  defaultOptions={calculatorDefaultOptions}
                  totalPrices={totalPrices}
                  projectDeadline={projectDeadline}
                  hourlyOptions={hourlyOptions}
                  onClearSelections={clearSelections}
                />
              </div>
            </div>
          </div>
        </div>

        <Summary
          companyName={companyName}
          sections={sections}
          selectedOptions={selectedOptions}
          selectedOptionsWithQuantities={selectedOptionsWithQuantities}
          defaultOptions={calculatorDefaultOptions}
          totalPrices={totalPrices}
          projectDeadline={projectDeadline}
          hourlyOptions={hourlyOptions}
          validateSelections={validateSelections}
        />
      </main>

      <footer className="app-footer">
        <div className="container">
          <p>{t('footer', selectedLocale)}</p>
        </div>
      </footer>

      <AgreementPopup
        isOpen={isAgreementOpen}
        onClose={() => setIsAgreementOpen(false)}
        companyName={companyName}
      />
    </div>
  );
}

function App() {
  return (
    <LocaleProvider>
      <CurrencyProvider>
        <AppContent />
      </CurrencyProvider>
    </LocaleProvider>
  );
}

export default App
